/*
 * Decléor — Animated quantity number (NumberFlow)
 * ---------------------------------------------------------------------------
 * Rolls the quantity number on change using NumberFlow (https://number-flow.barvian.me).
 * The library is SELF-HOSTED at assets/number-flow.js (a fully-bundled, dependency-free
 * build that auto-registers the <number-flow> custom element). Its URL is passed in via
 * data-numberflow-src on this script's own <script> tag.
 *
 * We load it as a first-party <script type="module"> (NOT a cross-origin import() and NOT
 * a CDN) so it is never blocked by the storefront CSP or by a browser content/ad blocker.
 *
 * Used on BOTH the product page (input changes in place → the input listener animates) and
 * the CART line items. The cart re-renders its line items via the Section Rendering API, so
 * the <number-flow> is replaced on every change — we therefore (a) re-scan on cart events and
 * (b) remember the previous quantity per line key (data-update-cart) so a freshly-rendered
 * flow rolls from the old value to the new one (otherwise it would just hard-set, no roll).
 *
 * The lib is loaded lazily the first time a .quantity__flow exists (so empty carts / pages
 * without a quantity selector never fetch it). Degrades gracefully: if it can't load, the
 * plain input number stays visible (.nf-ready, which hides it, is only added once bound).
 *
 * NumberFlow contract (verified against number-flow@0.6.0): `value` is read-only — drive it
 * via el.update(value); the FIRST update only sets (no roll), every SUBSEQUENT update rolls.
 */
(function () {
  'use strict';

  var nfScript = document.querySelector('script[data-numberflow-src]');
  var SRC = nfScript && nfScript.getAttribute('data-numberflow-src');
  if (!SRC) { return; } // No local lib URL → leave the plain input number visible.

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lastQ = {};        // cart line key → last quantity (persists across re-renders)
  var libPromise = null;

  function bind(flow) {
    if (flow.dataset.nfBound || typeof flow.update !== 'function') { return; }
    var wrapper = flow.closest('.quantity__wrapper');
    var input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) { return; }

    flow.dataset.nfBound = '1';
    if (reduce) { flow.animated = false; }

    var read = function () { return parseInt(input.value, 10) || 0; };
    var key = input.getAttribute('data-update-cart'); // present on cart lines, null on the product form
    var current = read();

    if (key && Object.prototype.hasOwnProperty.call(lastQ, key) && lastQ[key] !== current && !reduce) {
      // Cart line re-rendered with a changed quantity → roll from the previous value.
      flow.update(lastQ[key]);                                       // prime to old (no roll)
      requestAnimationFrame(function () { flow.update(current); });  // then roll to the new value
    } else {
      flow.update(current);                                          // prime
    }
    if (key) { lastQ[key] = current; }

    // Product page: the input value changes in place → animate on input/change.
    var sync = function () {
      var v = read();
      flow.update(v);
      if (key) { lastQ[key] = v; }
    };
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);

    // Reveal the overlay (and hide the plain input number) only now that it's bound.
    wrapper.classList.add('nf-ready');
  }

  function scan() { document.querySelectorAll('.quantity__flow').forEach(bind); }

  function ensureLib() {
    if (window.customElements && customElements.get('number-flow')) { return Promise.resolve(); }
    if (libPromise) { return libPromise; }
    libPromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.type = 'module';
      s.src = SRC;
      s.addEventListener('load', resolve);
      s.addEventListener('error', resolve); // resolve anyway; scan() simply finds nothing usable
      document.head.appendChild(s);
    }).then(function () {
      return window.customElements ? customElements.whenDefined('number-flow').catch(function () {}) : null;
    });
    return libPromise;
  }

  // Load + bind only when a quantity flow is actually present (product form or cart items).
  function refresh() {
    if (!document.querySelector('.quantity__flow')) { return; }
    ensureLib().then(scan);
  }

  var refreshPending = false;
  function scheduleRefresh() {
    if (refreshPending) { return; }
    refreshPending = true;
    requestAnimationFrame(function () { refreshPending = false; refresh(); });
  }

  // The cart injects re-rendered line items into [data-line-items] via the Section Rendering
  // API — and that fetch resolves AFTER theme:cart:change fires, with no event afterward. So a
  // fixed timer races the network. Instead, observe the (stable) container: its innerHTML is
  // replaced, the element itself isn't, so the observer fires the instant the new <number-flow>
  // lands. bind()/scan() are idempotent (nfBound guard), so re-scanning is safe.
  function watch() {
    document.querySelectorAll('[data-line-items]').forEach(function (c) {
      if (c.__nfObserved) { return; }
      c.__nfObserved = 1;
      new MutationObserver(scheduleRefresh).observe(c, { childList: true, subtree: true });
    });
  }

  refresh(); // product-page quantity (if present at load)
  watch();   // observe cart line-item containers (drawer + cart page)

  // Theme editor sections; and re-attach observers if a cart container appears later.
  // Shopify's native section-load fires on DOCUMENT after the new HTML lands; the theme's own
  // section update fires on WINDOW. (The old `document` listener for `theme:section:load` was
  // dead — that event is window-only.) watch()/refresh() are idempotent, so re-running is safe.
  document.addEventListener('shopify:section:load', function () { watch(); refresh(); });
  window.addEventListener('theme:section:update', function () { watch(); refresh(); });
  document.addEventListener('theme:cart:change', watch);
  document.addEventListener('theme:cart:reload', watch);
  document.addEventListener('theme:cart:init', watch);
})();
